import { useNavigate } from 'react-router-dom'
import { LogOut, User } from 'lucide-react'
import { CountingNumber } from '@/components/ui/counting-number'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { WalletBoldIcon } from '../sidebar-icons'
import { cn } from '@/lib/utils'
import { useLogout } from '@/hooks/use-logout'
import { useDepositModal } from '@/context/DepositModalContext'
import { resolvePlayerAssetUrl } from '@/lib/player-api'
import { Button } from '@/components/ui/button'
import { useGetProfileQuery } from '@/store/api/profileApi'
import { useHeaderWallet } from '@/hooks/use-header-wallet'
import { useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'

const DEFAULT_AVATAR_SRC = '/images/avatars/default-avatar.png'

export function AuthenticatedActions() {
	const logout = useLogout()
	const navigate = useNavigate()
	const { openDeposit } = useDepositModal()
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const { data: profileResponse } = useGetProfileQuery(undefined, { skip: !isAuthenticated })
	const wallet = useHeaderWallet()
	const profile = profileResponse?.data
	const avatarUrl = profile?.avatar_url ? resolvePlayerAssetUrl(profile.avatar_url) : DEFAULT_AVATAR_SRC

	return (
		<>
			<Button variant='primary' onClick={() => openDeposit(wallet.currencyCode)}>
				Deposit
			</Button>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button
						type='button'
						aria-label='Open user menu'
						className='flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border-[1.5px] border-primary bg-background p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 sm:hidden'
					>
						<img src={avatarUrl} alt={profile?.username ?? profile?.email ?? 'Profile'} className='block h-full w-full rounded-full object-cover' />
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align='end' side='bottom' sideOffset={10} className='min-w-[180px] sm:hidden'>
					<div className='px-2 py-1.5 text-xs text-muted-foreground'>
						<CountingNumber
							number={wallet.value}
							fromNumber={wallet.from}
							decimalPlaces={wallet.decimalPlaces}
							animate={wallet.animate}
							animationKey={wallet.animationKey}
							onAnimationComplete={wallet.onAnimationComplete}
							className='inline tabular-nums'
						/>{' '}
						{wallet.currencyCode}
					</div>
					<DropdownMenuSeparator />
					<DropdownMenuItem onClick={() => navigate('/profile')}>
						<User className='mr-2 h-4 w-4 text-muted-foreground' />
						Profile
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuItem onClick={logout} className='text-destructive focus:text-destructive'>
						<LogOut className='mr-2 h-4 w-4' />
						Logout
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<div className='relative hidden items-center sm:flex'>
				<div className='overflow-hidden rounded-xl bg-gradient-to-r from-primary-border via-primary-border/70 to-border p-[1.5px]'>
					<div className='flex items-center gap-2 rounded-[10px] bg-secondary py-[11px] pl-1 pr-14'>
						<span
							aria-hidden
							className={cn('h-4 w-0.5 rounded-full transition-[background-color,box-shadow]', 'bg-primary shadow-[0_0_8px_2px_hsl(var(--primary)/0.35)]')}
						/>
						<WalletBoldIcon className='mx-1 h-6 w-6 shrink-0 text-primary' />
						<CountingNumber
							number={wallet.value}
							fromNumber={wallet.from}
							decimalPlaces={wallet.decimalPlaces}
							animate={wallet.animate}
							animationKey={wallet.animationKey}
							onAnimationComplete={wallet.onAnimationComplete}
							className='text-sm font-medium tabular-nums tracking-[-0.21px] text-foreground'
						/>
						{wallet.currencyCode ? <span className='mr-2 text-sm font-semibold tracking-[-0.21px] text-muted-foreground'>{wallet.currencyCode}</span> : null}
					</div>
				</div>

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button
							type='button'
							aria-label='Open user menu'
							className='absolute right-1 top-1/2 z-10 flex h-[40px] w-[40px] -translate-y-1/2 items-center justify-center overflow-hidden rounded-full border-primary bg-primary-highlight p-0 outline outline-2 outline-offset-4 outline-primary'
						>
							<img src={avatarUrl} alt={profile?.username ?? profile?.email ?? 'Profile'} className='block h-full w-full rounded-full object-cover' />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end' side='bottom' sideOffset={10} className='min-w-[160px]'>
						<DropdownMenuItem onClick={() => navigate('/profile')}>
							<User className='mr-2 h-4 w-4 text-muted-foreground' />
							Profile
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={logout} className='text-destructive focus:text-destructive'>
							<LogOut className='mr-2 h-4 w-4' />
							Logout
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</>
	)
}
